import { Reveal, PageHero, SectionHead } from "../components/Reveal";
import { BrandMark } from "../components/Emblem";
import { IMAGES, LEGAL, PRINCIPLES } from "../data/site";
import useSeo from "../components/Seo";

function CorporateInfo() {
  const rows = [
    ["Company", "The Royal Mahaveer Roadlines Pvt Ltd"],
    ["CIN", LEGAL.cin],
    ["Status", LEGAL.status],
    ["Registrar of Companies", LEGAL.roc],
    ["Incorporation Date", LEGAL.incorporated],
    ["Registered Office", LEGAL.registeredOffice],
    ["Authorised Capital", LEGAL.authorisedCapital],
    ["Paid-up Capital", LEGAL.paidUpCapital],
  ];
  return (
    <section className="border-t border-gold/10 bg-surface py-24 sm:py-32" data-testid="corporate-info-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionHead overline="Corporate Information" title="Registered & regulated" />
        <Reveal delay={0.15}>
          <dl className="mt-14 grid gap-px border border-gold/15 bg-gold/15 sm:grid-cols-2">
            {rows.map(([k, v]) => (
              <div key={k} className="bg-ink p-7">
                <dt className="text-[10px] font-semibold uppercase tracking-[0.24em] text-gold">{k}</dt>
                <dd className="mt-2.5 text-sm leading-relaxed text-foreground/90">{v}</dd>
              </div>
            ))}
          </dl>
        </Reveal>
      </div>
    </section>
  );
}

export default function About() {
  useSeo("About Us", "The Royal Mahaveer Roadlines Pvt Ltd — a professional road transportation and logistics company built on dependable movement and long-term relationships.");
  return (
    <>
      <PageHero
        overline="About the Company"
        lines={[<>A name that stands for</>, <><em className="italic text-gold-light">strength</em> on the road</>]}
        desc="Our emblem says it in one mark — a road rising to a diamond. Every journey powered, every destination earned."
      />

      <section className="py-24 sm:py-32" data-testid="about-story-section">
        <div className="mx-auto grid max-w-7xl items-start gap-16 px-6 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="overline-gold">Our Story</p>
              <h2 className="mt-5 font-heading text-3xl font-medium leading-tight sm:text-4xl">
                Professional transportation, <em className="italic text-gold-light">responsibly run</em>
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-10 space-y-6">
                <p className="text-base leading-[1.9] text-foreground/85 sm:text-lg">
                  The Royal Mahaveer Roadlines Pvt Ltd is a professional road transportation
                  and logistics company, incorporated in Bengaluru, Karnataka.
                </p>
                <p className="max-w-2xl text-sm leading-[1.9] text-muted-foreground sm:text-base">
                  We move cargo, commercial vehicles, heavy loads and specialized consignments across
                  India — every movement planned, coordinated and monitored with care, because our
                  customers' businesses depend on what we carry.
                </p>
                <p className="max-w-2xl text-sm leading-[1.9] text-muted-foreground sm:text-base">
                  Our focus is long-term relationships built through professional service and
                  responsible operations — not one-time transactions.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="mt-12 flex flex-wrap items-center gap-x-10 gap-y-3 border-t border-foreground/10 pt-8" data-testid="about-fact-strip">
                {["HQ — Bengaluru, Karnataka", "Pan-India Network", "Incorporated 2026"].map((t) => (
                  <span key={t} className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.26em] text-foreground/60">
                    <span className="h-1 w-1 rotate-45 bg-gold/70" />
                    {t}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.2} className="group lg:col-span-5">
            <div className="overflow-hidden border border-gold/20">
              <img src={IMAGES.yard} alt={IMAGES.yardAlt} className="img-spotlight aspect-[4/5] w-full object-cover" loading="lazy" />
            </div>
            <div className="mt-7 flex items-center gap-4">
              <BrandMark className="h-12 w-12" />
              <p className="font-heading text-sm italic leading-relaxed text-muted-foreground">
                “A road rising to its mark — <em className="not-italic font-semibold text-gold-light">power</em> in every journey.”
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-gold/10 py-24 sm:py-32" data-testid="about-values-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <SectionHead overline="What Guides Us" title="Six principles, one standard" />
          <div className="mt-16 grid gap-x-12 gap-y-14 sm:grid-cols-2 lg:grid-cols-3">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.num} delay={0.07 * i}>
                <div className="border-t border-gold/25 pt-6">
                  <span className="font-heading text-3xl text-gold/70">{p.num}</span>
                  <h3 className="mt-4 text-sm font-bold uppercase tracking-[0.22em]">{p.title}</h3>
                  <p className="mt-3 text-sm leading-[1.8] text-muted-foreground">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CorporateInfo />
    </>
  );
}
